import { OrganizationAccessStatus, ProjectType } from '@lightdash/common';
import { Box, useComputedColorScheme } from '@mantine/core';
import { clsx } from 'clsx';
import { memo } from 'react';
import useDashboardStorage from '../../hooks/dashboard/useDashboardStorage';
import { useOrganizationAccess } from '../../hooks/organization/useOrganizationAccess';
import { useActiveProjectUuid } from '../../hooks/useActiveProject';
import { useProject } from '../../hooks/useProject';
import { useProjectUuid } from '../../hooks/useProjectUuid';
import { useImpersonation } from '../../hooks/user/useImpersonation';
import useFullscreen from '../../providers/Fullscreen/useFullscreen';
import MantineBaseProvider from '../../providers/MantineBaseProvider';
import { isPlaygroundProvisioningSource } from '../../utils/playgroundProject';
import { getProjectUrlIdentifier } from '../../utils/projectUrl';
import { rdLightNavbar } from '../../rd/embedded'; // RD:
import { BANNER_HEIGHT, NAVBAR_HEIGHT } from '../common/Page/constants';
import { DashboardExplorerBanner } from './DashboardExplorerBanner';
import { ImpersonationBanner } from './ImpersonationBanner';
import classes from './index.module.css';
import { MainNavBarContent } from './MainNavBarContent';
import { PreviewBanner } from './PreviewBanner';
import { TrialWarningBanner } from './TrialWarningBanner';

enum NavBarMode {
    DEFAULT = 'default',
    EDITING_DASHBOARD_CHART = 'editingDashboardChart',
}

const useNavBarMode = () => {
    const { isEditingDashboardChart } = useDashboardStorage();

    return {
        navBarMode: isEditingDashboardChart
            ? NavBarMode.EDITING_DASHBOARD_CHART
            : NavBarMode.DEFAULT,
    };
};

interface NavBarProps {
    isFixed?: boolean;
}

const NavBarContent = ({
    navBarMode,
    activeProjectUuid,
    activeProjectUrlIdentifier,
    isLoadingActiveProject,
}: {
    navBarMode: NavBarMode;
    activeProjectUuid: string | undefined;
    activeProjectUrlIdentifier: string | undefined;
    isLoadingActiveProject: boolean;
}) => {
    const projectUuid = useProjectUuid();
    if (navBarMode === NavBarMode.EDITING_DASHBOARD_CHART) {
        return <DashboardExplorerBanner projectUuid={projectUuid} />;
    }

    return (
        <MainNavBarContent
            activeProjectUuid={activeProjectUuid}
            activeProjectUrlIdentifier={activeProjectUrlIdentifier}
            isLoadingActiveProject={isLoadingActiveProject}
        />
    );
};

const getNavBarRootElement = () =>
    document.getElementById('navbar-header') ?? undefined;

const NavBar = memo(({ isFixed = true }: NavBarProps) => {
    const { isFullscreen } = useFullscreen();
    const rdLight = rdLightNavbar(useComputedColorScheme('light')); // RD: light top bar in the RD app's light theme
    const routeProjectUuid = useProjectUuid();

    const { activeProjectUuid, isLoading: isLoadingActiveProject } =
        useActiveProjectUuid({
            refetchOnMount: true,
            projectUuid: routeProjectUuid,
        });
    const { data: project } = useProject(activeProjectUuid);

    const isCurrentProjectPreview = project?.type === ProjectType.PREVIEW;
    const isCurrentProjectPlayground = isPlaygroundProvisioningSource(
        project?.provisioningSource,
    );
    const upstreamProjectUuid = isCurrentProjectPreview
        ? project?.upstreamProjectUuid
        : undefined;
    const { data: upstreamProject } = useProject(upstreamProjectUuid);
    // A learner's copy of the training project is not a preview in the
    // engineering sense: no branch, no upstream to promote to. No banner.
    const isTrainingCopy =
        isCurrentProjectPreview &&
        upstreamProject?.type === ProjectType.TRAINING;
    const { isImpersonating } = useImpersonation();
    const { data: organizationAccess } = useOrganizationAccess();

    const { navBarMode } = useNavBarMode();

    const showTrialWarning =
        !isImpersonating &&
        !isCurrentProjectPreview &&
        !isCurrentProjectPlayground &&
        (organizationAccess?.status ===
            OrganizationAccessStatus.TRIAL_WARNING ||
            organizationAccess?.status ===
                OrganizationAccessStatus.TRIAL_EXPIRED);

    const hasBanner =
        isImpersonating ||
        (isCurrentProjectPreview && !isTrainingCopy) ||
        showTrialWarning;

    // Calculate placeholder height: navbar + banner.
    const headerContainerHeight =
        NAVBAR_HEIGHT + (hasBanner ? BANNER_HEIGHT : 0);

    // Scoped dark theme for the navbar using Mantine's cssVariablesSelector + getRootElement.
    // This is the recommended approach for scoped theming, though it has known CSS specificity
    // limitations (see: https://github.com/orgs/mantinedev/discussions/4803).
    // The manual `data-mantine-color-scheme="dark"` attribute helps CSS selectors match correctly.
    return (
        <Box
            id="navbar-header"
            data-mantine-color-scheme={rdLight ? 'light' : 'dark'} // RD:
            data-has-visible-fixed-navbar={isFixed && !isFullscreen}
            data-has-banner={hasBanner}
        >
            <MantineBaseProvider
                forceColorScheme={rdLight ? 'light' : 'dark'} // RD:
                cssVariablesSelector="#navbar-header"
                getRootElement={getNavBarRootElement}
            >
                {isImpersonating ? (
                    <ImpersonationBanner />
                ) : isCurrentProjectPreview && !isTrainingCopy ? (
                    <PreviewBanner
                        expiresAt={project?.expiresAt ?? null}
                        upstreamProject={
                            upstreamProject
                                ? {
                                      projectUuid: upstreamProject.projectUuid,
                                      slug: upstreamProject.slug,
                                      name: upstreamProject.name,
                                  }
                                : null
                        }
                    />
                ) : (
                    organizationAccess && (
                        <TrialWarningBanner access={organizationAccess} />
                    )
                )}
                <Box
                    component="header"
                    h={NAVBAR_HEIGHT}
                    className={clsx(
                        classes.header,
                        isFixed && classes.fixed,
                        rdLight && classes.headerRdLight, // RD:
                        navBarMode === NavBarMode.EDITING_DASHBOARD_CHART &&
                            classes.headerEditingDashboardChart,
                    )}
                    mt={hasBanner ? BANNER_HEIGHT : 0}
                    display={isFullscreen ? 'none' : 'flex'}
                    px="md"
                >
                    <NavBarContent
                        navBarMode={navBarMode}
                        activeProjectUuid={activeProjectUuid}
                        activeProjectUrlIdentifier={
                            project
                                ? getProjectUrlIdentifier(project)
                                : activeProjectUuid
                        }
                        isLoadingActiveProject={isLoadingActiveProject}
                    />
                </Box>
                {/* Placeholder to reserve space when navbar is fixed */}
                {isFixed && !isFullscreen && <Box h={headerContainerHeight} />}
            </MantineBaseProvider>
        </Box>
    );
});

export default NavBar;
